import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import SessionEndDialog from "@memorang/ui/components/StudySession/SessionEndDialog";
import PteSummativeAppbar from "@memorang/ui/components/StudySession/StudySessionAppbar/SummativeAppbar/Pte";
import ToeicSummativeAppbar from "@memorang/ui/components/StudySession/StudySessionAppbar/SummativeAppbar/Toeic";
import SummativeSessionAppbar from "@memorang/ui/components/StudySession/StudySessionAppbar/SummativeSessionAppbar";
import FlightCalculatorDialog from "components/FlightCalculator/FlightCalculatorDialog";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioPlaybackTestDialog } from "../components/AudioPlaybackTestDialog";
import DialogSession from "../components/SessionDialog";
import { useSessionItemsNavLayout } from "../contexts/SessionItemsNavLayoutContext";
import {
	isIntroScreen,
	isIntroScreenWithoutAppBar,
} from "../helpers/intro-screens";
import { getSummativeResponseConfig } from "../helpers/response-timing";
import useCurrentSession from "../hooks/useCurrentSession";
import useEditorStore from "../hooks/useEditorStore";
import useExitReview from "../hooks/useExitReview";
import useSessionAppBarHandlers from "../hooks/useSessionAppBarHandlers";
import useSessionAppBarState from "../hooks/useSessionAppBarState";
import useTestDriverAppbarProps from "../hooks/useTestDriverAppbarProps";
import { buildQuestionsConfig } from "./StudySessionBottombarContainer/button-config";

type Props = {
	showStartDialog?: boolean;
	showAudioPlaybackTestDialog?: boolean;
	onCloseAudioPlaybackTestDialog: () => void;
};

const SummativeAppBarContainer = ({
	showStartDialog,
	showAudioPlaybackTestDialog,
	onCloseAudioPlaybackTestDialog,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { t } = useTranslation();
	const [showFlightCalculator, setShowFlightCalculator] = useState(false);
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
	const [showSummativeEndSessionDialog, setShowSummativeEndSessionDialog] =
		useState(false);
	const [showBrowserBackWarning, setShowBrowserBackWarning] = useState(false);
	const [showTimeExpiredDialog, setShowTimeExpiredDialog] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);

	useFocusEffect(
		useCallback(() => {
			setHasFocus(true);
			return () => setHasFocus(false);
		}, []),
	);

	const isTimerPausingDialogOpen =
		showStartDialog ||
		showAudioPlaybackTestDialog ||
		showSummativeEndSessionDialog;

	const s = useSessionAppBarState(isTimerPausingDialogOpen);
	const handleExitReview = useExitReview();
	const editorRef = useEditorStore((store) => store.editorRef);
	const { currentItem } = useCurrentSession();
	const sessionItemsNav = useSessionItemsNavLayout();
	const allowBackNavigation =
		getSummativeResponseConfig(s.currentItem?.response, !s.inReviewMode)
			?.allowPrevNavigation ?? true;
	const questionsMenuConfig = allowBackNavigation
		? buildQuestionsConfig(sessionItemsNav, true, t)
		: undefined;

	// Intro-only blocks have no question items; count the screens themselves
	// so the app bar reads "1 of 1" instead of "0 of 0".
	const numCurrentSessionItems = useMemo(() => {
		return (
			s.currentBlock.filter((item) => !isIntroScreen(item)).length ||
			s.currentBlock.length
		);
	}, [s.currentBlock]);

	const toggeShowEndSessionDialog = useCallback(() => {
		if (s.inReviewMode) {
			router.back();
			return;
		}
		setShowEndSessionDialog((prev) => !prev);
	}, [s.inReviewMode]);

	const handlers = useSessionAppBarHandlers({
		hasFocus,
		isOlxSession: s.isOlxSession,
		isTimeExpired: s.isTimeExpired,
		isSummativeTest: true,
		inReviewMode: s.inReviewMode,
		hasAnsweredAtLeastOneItem: s.hasAnsweredAtLeastOneItem,
		sessionId: s.sessionId,
		sessionNumericId: s.sessionNumericId,
		projectId: s.projectId,
		organizationId: s.organizationId,
		examId: s.examId,
		examName: s.examName,
		currentItemId: s.currentItemId,
		totalTime: s.totalTime,
		isMarked: s.isMarked,
		showDialog: isTimerPausingDialogOpen,
		numSections: s.numSections,
		numTotalItems: s.numTotalItems,
		sessionSubType: s.sessionSubType,
		diagnosticLabel: s.diagnosticLabel,
		onToggleTimeExpiredDialog: () => setShowTimeExpiredDialog((prev) => !prev),
		onToggleEndSessionDialog: toggeShowEndSessionDialog,
		onToggleSummativeEndSessionDialog: () =>
			setShowSummativeEndSessionDialog((prev) => !prev),
		onToggleBrowserBackWarning: setShowBrowserBackWarning,
		onToggleFlightCalculator: setShowFlightCalculator,
	});

	const {
		isPteDriver,
		isToeicDriver,
		hasListeningItems,
		appLogo,
		toeicQuestionStates,
		isReadingSkill,
	} = useTestDriverAppbarProps(s);

	const isUntimedIntroScreen = isIntroScreenWithoutAppBar(currentItem);
	if (isUntimedIntroScreen && !isPteDriver) {
		return null;
	}

	const handlePauseSession =
		s.inReviewMode || isUntimedIntroScreen
			? undefined
			: handlers.handleClickRightCta;

	const partProgressLabel = s.hasParts
		? `Part ${s.currentPartIndex + 1} of ${s.numTotalParts}`
		: undefined;

	return (
		<>
			{isPteDriver ? (
				<PteSummativeAppbar
					testTitle={s.examName ?? ""}
					sectionLabel={s.currentPartName || s.sectionName}
					partProgressLabel={
						isUntimedIntroScreen ? undefined : partProgressLabel
					}
					currentItemIndex={s.displayItemIndex}
					numCurrentSessionItems={numCurrentSessionItems}
					blocks={isUntimedIntroScreen ? [] : s.partScopedBlocks}
					currentBlockIndex={s.sectionIndexWithinPart}
					numTotalBlocks={s.partScopedBlocks.length}
					currentProgress={
						isUntimedIntroScreen
							? s.displayItemIndex / Math.max(1, numCurrentSessionItems)
							: (s.blocks[s.currentBlockIndex]?.currentProgress ?? 0)
					}
					sessionTime={
						s.inReviewMode || isUntimedIntroScreen ? undefined : s.formattedTime
					}
					timerScope={s.timerScope}
					isTimerExpired={s.isNegative}
					showSectionProgress={
						isUntimedIntroScreen || !isIntroScreen(currentItem)
					}
					showItemCount={isUntimedIntroScreen || !isIntroScreen(currentItem)}
					showSectionLabel={!isUntimedIntroScreen}
					onBackPress={handlePauseSession}
					onPauseSession={handlePauseSession}
				/>
			) : isToeicDriver ? (
				<ToeicSummativeAppbar
					logo={{ light: appLogo.light, dark: appLogo.dark }}
					testName={s.examName ?? ""}
					section={s.sectionName || s.skillName || ""}
					onQuit={handlePauseSession}
					onExit={s.inReviewMode ? handleExitReview : undefined}
					progressBar={{
						currentQuestion: s.displayItemIndex,
						totalQuestions: numCurrentSessionItems,
						questionStates: toeicQuestionStates,
						timeLeftSeconds: Math.max(0, s.timerTotalSeconds),
						variant: isReadingSkill ? "reading" : "listening",
						listeningProgress: s.blocks[0]?.currentProgress ?? 0,
						showTimer: !s.inReviewMode,
					}}
				/>
			) : (
				<SummativeSessionAppbar
					handleClickRightCta={handlers.handleClickRightCta}
					handleClickMenuItem={handlers.handleClickMenuItem}
					handleClickToolbarActions={(type) =>
						handlers.handleToolbarActions(type, editorRef)
					}
					userName={s.username ?? ""}
					numericId={s.sessionNumericId}
					examName={s.examName ?? ""}
					timeRemaining={s.formattedTime}
					showPause={!s.isOlxSession}
					isTimerExpired={s.isNegative}
					inReviewMode={s.inReviewMode}
					blocks={s.partScopedBlocks}
					currentBlockIndex={s.sectionIndexWithinPart}
					numTotalBlocks={s.partScopedBlocks.length}
					partProgressLabel={partProgressLabel}
					numCurrentSessionItems={numCurrentSessionItems}
					currentItemIndex={s.displayItemIndex}
					skillName={s.skillName}
					isMarked={Boolean(s.isMarked)}
					showToolbar={Boolean(s.showToolbar)}
					showCalculator={Boolean(s.shouldShowFlightCalculator)}
					tooltips={{
						calculator: t("session.toolbar.calculatorTooltip"),
						highlight: t("session.toolbar.highlightTooltip"),
						mark: t("session.toolbar.markTooltip"),
						unmark: t("session.toolbar.unmarkTooltip"),
					}}
					questionsMenuConfig={questionsMenuConfig}
				/>
			)}
			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggeShowEndSessionDialog}
					handleAction={handlers.handleNavigation}
					hasAnsweredAtLeastOneItem={s.hasAnsweredAtLeastOneItem}
					isDiagnostic={s.isDiagnostic}
					numItemsLeft={s.numItemsLeft}
				/>
			)}
			{showSummativeEndSessionDialog && (
				<DialogSession
					type="quit"
					open={showSummativeEndSessionDialog}
					handleClose={() => setShowSummativeEndSessionDialog(false)}
					handleClickButton={handlers.handleClickButton}
					confirmLoading={handlers.isPausing}
					timeRemainingSeconds={Math.max(0, s.timerTotalSeconds)}
					numItemsLeft={s.numItemsLeft}
				/>
			)}
			{showTimeExpiredDialog && (
				<DialogSession
					type="timerExpired"
					maxWidth="xs"
					open={showTimeExpiredDialog}
					showCloseButton={false}
					handleClose={() => setShowTimeExpiredDialog(false)}
				/>
			)}
			{showFlightCalculator && (
				<FlightCalculatorDialog
					dialogVisible={showFlightCalculator}
					hideDialog={() => setShowFlightCalculator(false)}
				/>
			)}
			{hasListeningItems &&
				showAudioPlaybackTestDialog &&
				!showStartDialog &&
				!s.inReviewMode && (
					<AudioPlaybackTestDialog
						showDialog={Boolean(showAudioPlaybackTestDialog)}
						handleClose={onCloseAudioPlaybackTestDialog}
					/>
				)}
			{showBrowserBackWarning && handlers.exitDialogCopy && (
				<ConfirmDialog
					visible={showBrowserBackWarning}
					title={handlers.exitDialogCopy.title}
					icon="alert-circle-outline"
					body={handlers.exitDialogCopy.body}
					dismissCallback={handlers.handleConfirmExit}
					dismissCta={handlers.exitDialogCopy.dismissCta}
					confirmCallback={handlers.handleCancelExit}
					confirmCta={handlers.exitDialogCopy.confirmCta}
					maxWidth="xs"
				/>
			)}
		</>
	);
};

export default SummativeAppBarContainer;
